import React, { useState, useRef } from 'react';
import { useAssets } from './useAssets.ts';

interface Props {
  pageId: string;
  pageName: string;
  onClose: () => void;
}

export function PublishPostModal({ pageId, pageName, onClose }: Props) {
  const { publishPost } = useAssets();
  
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [isDragging, setIsDragging] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = (newFiles: FileList | File[]) => {
    const validFiles = Array.from(newFiles).filter(f => f.type.startsWith('image/'));
    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
      setPreviewUrls(prev => [
        ...prev,
        ...validFiles.map(f => URL.createObjectURL(f))
      ]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      addFiles(e.dataTransfer.files);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handlePublish = async () => {
    if (!message.trim() && files.length === 0) {
      setError('Додайте текст або фото');
      return;
    }
    
    setIsPublishing(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await publishPost(pageId, message, files);
      setSuccess(`Опубліковано успішно! ID поста: ${res.post_id || res.id}`);
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Помилка публікації');
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div 
        className="modal-content glass-panel" 
        onClick={(e) => e.stopPropagation()} 
        style={{ maxWidth: '500px', width: '100%', padding: 'var(--space-5)' }}
      >
        <h3 style={{ marginBottom: 'var(--space-2)' }}>Публікація поста</h3>
        <p className="text-subtle text-sm" style={{ marginBottom: 'var(--space-4)' }}>
          Сторінка: <span className="text-normal">{pageName}</span>
        </p>

        {error && (
          <div className="notification notification--error" style={{ marginBottom: 'var(--space-4)' }}>
            {error}
          </div>
        )}

        {success && (
          <div className="notification" style={{ marginBottom: 'var(--space-4)', background: 'var(--color-primary)', color: '#fff', borderColor: 'var(--color-primary)' }}>
            {success}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div>
            <label className="text-sm font-medium" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>Текст поста</label>
            <textarea
              className="input"
              style={{ width: '100%', minHeight: '120px', resize: 'vertical' }}
              placeholder="Що у вас нового?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>Фото (можна декілька)</label>
            <input 
              type="file" 
              accept="image/*" 
              multiple
              style={{ display: 'none' }} 
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            
            <div 
              style={{ 
                border: `2px dashed ${isDragging ? 'var(--color-primary)' : 'var(--color-border)'}`, 
                padding: 'var(--space-4)', 
                textAlign: 'center',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                background: isDragging ? 'var(--color-surface-2)' : 'transparent',
                transition: 'all 0.2s'
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="text-subtle text-sm">
                Натисніть або перетягніть фото сюди (Drag & Drop)
              </div>
            </div>

            {previewUrls.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                {previewUrls.map((url, i) => (
                  <div key={url} style={{ position: 'relative', paddingTop: '100%' }}>
                    <img 
                      src={url} 
                      alt={`Preview ${i}`} 
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-sm)', background: 'var(--color-surface-1)' }} 
                    />
                    <button 
                      className="btn btn--danger" 
                      style={{ position: 'absolute', top: 4, right: 4, padding: '2px 6px', fontSize: '0.7rem' }}
                      onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', marginTop: 'var(--space-5)' }}>
          <button className="btn btn--outline" onClick={onClose} disabled={isPublishing}>Скасувати</button>
          <button className="btn btn--primary" onClick={handlePublish} disabled={isPublishing}>
            {isPublishing ? 'Публікація...' : 'Опублікувати'}
          </button>
        </div>
      </div>
    </div>
  );
}
